/**
 * Diálogo para cambiar un alimento de una comida planeada por un equivalente.
 * Llama a /api/macro-log/swap-options con:
 *   - food_name, food_category, current_macros (del item actual con sus gramos)
 *   - meal_capacity = target_macros del plato - suma de los OTROS items
 * Muestra alternativas con gramos exactos calculados y warnings si excede.
 * Al confirmar, hace POST /api/macro-log/swaps para persistir el cambio.
 *
 * Importante: la usuaria NO puede cambiar gramos. Solo elegir entre opciones
 * ya calculadas. Solo el admin puede modificar macros desde el plan.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { FaExchangeAlt, FaCheck, FaExclamationTriangle, FaTimes, FaUndo, FaPlus, FaTrash } from 'react-icons/fa';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem('deisycfit_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function SwapFoodDialog({
  open, onOpenChange, food, foodCategory, currentMacros, currentGrams, mealCapacity,
  dietTab, mealIndex, optionIndex = 0, itemIndex, hasExistingSwap, onSaved, today,
}) {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [primaryMacro, setPrimaryMacro] = useState('protein');
  const [applying, setApplying] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [optionsSearch, setOptionsSearch] = useState('');

  const fetchOptions = useCallback(async () => {
    if (!food || !open) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/macro-log/swap-options`, {
        food_name: food, food_category: foodCategory,
        current_macros: currentMacros, current_grams: currentGrams,
        meal_capacity: mealCapacity,
      }, { headers: authHeaders() });
      setOptions(res.data?.options || []);
      setPrimaryMacro(res.data?.primary_macro || 'protein');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No pudimos cargar reemplazos');
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [food, foodCategory, currentMacros, mealCapacity, open]);

  useEffect(() => { fetchOptions(); }, [fetchOptions]);
  // Limpiar buscador cada vez que abre el diálogo o cambia el alimento
  useEffect(() => { if (open) setOptionsSearch(''); }, [open, food]);

  // Normalizador simple (minúsculas + sin tildes) para el filtro
  const _norm = (s) => (s || '').toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const filteredOptions = optionsSearch.trim().length === 0
    ? options
    : options.filter(o => _norm(o.name).includes(_norm(optionsSearch)));

  const applySwap = async (opt) => {
    if (opt.warning) {
      const msg = `${opt.warning}\n\nSi continúas, el sistema ajustará automáticamente los otros alimentos de esta comida para compensar.\n\n¿Hacer el cambio?`;
      if (!window.confirm(msg)) return;
    }
    setApplying(true);
    try {
      await axios.post(`${API}/macro-log/swaps`, {
        date: today, diet_tab: dietTab, meal_index: mealIndex,
        option_index: optionIndex, item_index: itemIndex,
        original_food_name: food, new_food_name: opt.name,
        grams: opt.grams, unit: opt.unit, macros: opt.macros,
      }, { headers: authHeaders() });
      toast.success(`Cambiado por ${opt.name}`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar el cambio');
    } finally {
      setApplying(false);
    }
  };

  const removeSwap = async () => {
    setRemoving(true);
    try {
      await axios.delete(`${API}/macro-log/swaps`, {
        params: { date_param: today, diet_tab: dietTab, meal_index: mealIndex, option_index: optionIndex, item_index: itemIndex },
        headers: authHeaders(),
      });
      toast.success('Restaurado el alimento original');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error('No se pudo restaurar');
    } finally {
      setRemoving(false);
    }
  };

  const macroLabel = (m) => m === 'protein' ? 'proteína' : m === 'carbs' ? 'carbos' : m === 'fats' ? 'grasas' : 'calorías';

  // ===== Agregar mi alimento propio =====
  const [showAddForm, setShowAddForm] = useState(false);
  const [myFood, setMyFood] = useState({ name: '', calories: '', protein: '', carbs: '', fats: '' });
  const [savingMyFood, setSavingMyFood] = useState(false);
  // Buscador en vivo dentro del form
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiConfidence, setAiConfidence] = useState(null);

  // Búsqueda con debounce: primero local, si no hay resultados consulta búsqueda inteligente sola.
  useEffect(() => {
    if (!showAddForm) return;
    const q = (myFood.name || '').trim();
    if (q.length < 2) { setSearchResults([]); setAiResult(null); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/macro-log/foods/search`, {
          params: { q, limit: 8 },
          headers: authHeaders(),
        });
        const items = res.data?.items || [];
        setSearchResults(items);
        // Si no hay resultados locales, disparar automáticamente búsqueda inteligente.
        if (items.length === 0 && !aiResult && !aiLoading) {
          await aiLookupAuto(q);
        }
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [myFood.name, showAddForm]); // eslint-disable-line

  const aiLookupAuto = async (name) => {
    let hint = (foodCategory || '').toLowerCase();
    if (['proteina','proteinas','prot'].includes(hint)) hint = 'protein';
    else if (['carbohidrato','carbohidratos','carbo','carbos'].includes(hint)) hint = 'carbs';
    else if (['grasa','grasas','lipidos'].includes(hint)) hint = 'fats';
    else if (['vegetal','vegetales','verdura','verduras'].includes(hint)) hint = 'vegetable';
    else if (['fruta','frutas'].includes(hint)) hint = 'fruit';
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/macro-log/ai-lookup-food`, {
        name, category_hint: hint || undefined,
      }, { headers: authHeaders() });
      const d = res.data || {};
      setMyFood({
        name: d.name || name,
        calories: String(Math.round(d.calories ?? 0)),
        protein: String(+(d.protein ?? 0)),
        carbs: String(+(d.carbs ?? 0)),
        fats: String(+(d.fats ?? 0)),
      });
      setAiResult(d);
    } catch {
      // silencioso: si no encuentra, la clienta puede llenar a mano
    } finally {
      setAiLoading(false);
    }
  };

  const pickSearchResult = (food) => {
    setMyFood({
      name: food.name || myFood.name,
      calories: String(Math.round(food.calories ?? 0)),
      protein: String(+(food.protein ?? 0)),
      carbs: String(+(food.carbs ?? 0)),
      fats: String(+(food.fats ?? food.fat ?? 0)),
    });
    setSearchResults([]);
    setAiResult(null);
  };

  const aiLookup = async () => {
    const name = (myFood.name || '').trim();
    if (name.length < 2) { toast.error('Escribe el nombre del alimento primero'); return; }
    // Pista de categoría = la del item original
    let hint = (foodCategory || '').toLowerCase();
    if (['proteina','proteinas','prot'].includes(hint)) hint = 'protein';
    else if (['carbohidrato','carbohidratos','carbo','carbos'].includes(hint)) hint = 'carbs';
    else if (['grasa','grasas','lipidos'].includes(hint)) hint = 'fats';
    else if (['vegetal','vegetales','verdura','verduras'].includes(hint)) hint = 'vegetable';
    else if (['fruta','frutas'].includes(hint)) hint = 'fruit';

    setAiLoading(true);
    setAiResult(null);
    try {
      const res = await axios.post(`${API}/macro-log/ai-lookup-food`, {
        name, category_hint: hint || undefined,
      }, { headers: authHeaders() });
      const d = res.data || {};
      setMyFood({
        name: d.name || name,
        calories: String(Math.round(d.calories ?? 0)),
        protein: String(+(d.protein ?? 0)),
        carbs: String(+(d.carbs ?? 0)),
        fats: String(+(d.fats ?? 0)),
      });
      setAiResult(d);
      setAiConfidence(d.confidence || 'media');
      toast.success(`Macros encontrados (confianza ${d.confidence || 'media'})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No pudimos encontrar este alimento. Intenta con otro nombre.');
    } finally {
      setAiLoading(false);
    }
  };

  const submitMyFood = async () => {
    const name = (myFood.name || '').trim();
    const cal = parseFloat(myFood.calories);
    const p = parseFloat(myFood.protein) || 0;
    const c = parseFloat(myFood.carbs) || 0;
    const f = parseFloat(myFood.fats) || 0;
    if (!name) { toast.error('Ponle un nombre'); return; }
    if (!cal || cal <= 0) { toast.error('Las calorías deben ser mayores a 0'); return; }
    // Categoría = categoría del alimento que está cambiando
    // Normalizar foodCategory para mandar la clave canónica
    let cat = (foodCategory || '').toLowerCase();
    if (['proteina','proteinas','prot'].includes(cat)) cat = 'protein';
    else if (['carbohidrato','carbohidratos','carbo','carbos'].includes(cat)) cat = 'carbs';
    else if (['grasa','grasas','lipidos'].includes(cat)) cat = 'fats';
    else if (['vegetal','vegetales','verdura','verduras'].includes(cat)) cat = 'vegetable';
    else if (['fruta','frutas'].includes(cat)) cat = 'fruit';
    // Fallback al macro principal si la categoría no es reconocible
    const validCats = ['protein', 'carbs', 'fats', 'vegetable', 'fruit'];
    if (!validCats.includes(cat)) {
      cat = primaryMacro === 'carbs' ? 'carbs'
          : primaryMacro === 'fats' ? 'fats'
          : 'protein';
    }

    setSavingMyFood(true);
    try {
      await axios.post(`${API}/macro-log/my-foods`, {
        name, category: cat, calories: cal, protein: p, carbs: c, fats: f,
      }, { headers: authHeaders() });
      toast.success(`"${name}" agregado a tus alimentos`);
      setMyFood({ name: '', calories: '', protein: '', carbs: '', fats: '' });
      setShowAddForm(false);
      // Recargar opciones para incluir el nuevo
      fetchOptions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar tu alimento');
    } finally {
      setSavingMyFood(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-hidden flex flex-col" style={{ backgroundColor: '#111111', border: '1px solid rgba(255,255,255,0.1)' }}>
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <FaExchangeAlt className="text-pink-400" /> Cambiar &ldquo;{food}&rdquo; por...
          </DialogTitle>
          <DialogDescription className="text-gray-300">
            Elige una opción equivalente. El sistema ya calculó los gramos exactos para que tus macros se mantengan.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 space-y-2 mt-2">
          {loading && <p className="text-sm text-gray-400 text-center py-6">Calculando reemplazos...</p>}

          {!loading && options.length === 0 && (
            <div className="p-4 rounded-lg bg-white/5 border border-white/10 text-sm text-gray-300 text-center">
              No encontramos equivalencias para este alimento.
            </div>
          )}

          {!loading && options.length > 0 && (
            <div className="sticky top-0 z-10 pb-2 -mx-1 px-1" style={{ background: '#111111' }}>
              <Input
                type="text"
                placeholder={`Buscar entre ${options.length} alimentos...`}
                value={optionsSearch}
                onChange={(e) => setOptionsSearch(e.target.value)}
                className="bg-black/50 border-white/15 text-white text-sm"
                data-testid="swap-options-search"
              />
              {optionsSearch.trim() && (
                <p className="text-[11px] text-gray-400 mt-1 px-1">
                  {filteredOptions.length} coincidencia{filteredOptions.length === 1 ? '' : 's'} para &quot;{optionsSearch}&quot;
                </p>
              )}
            </div>
          )}

          {!loading && !showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full p-3 rounded-lg border border-dashed border-pink-500/40 text-pink-300 hover:bg-pink-500/10 transition-all flex items-center justify-center gap-2 text-sm font-semibold"
              data-testid="add-my-food-btn"
            >
              <FaPlus /> ¿No está tu alimento? Agregarlo a mis alimentos
            </button>
          )}

          {!loading && showAddForm && (
            <div className="p-3 rounded-lg border border-pink-500/40 bg-pink-500/5 space-y-2" data-testid="add-my-food-form">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-pink-300">Agregar mi alimento</p>
                <button onClick={() => { setShowAddForm(false); setSearchResults([]); setAiResult(null); }} className="text-xs text-gray-400 hover:text-white">
                  Cancelar
                </button>
              </div>
              <p className="text-[11px] text-gray-300 leading-snug">
                Escribe el nombre y te buscamos los macros automáticamente. Valores por <strong>100g</strong>.
              </p>
              <div className="relative">
                <Input
                  placeholder="Ej. Atún en aceite, Quinoa, Yogur natural..."
                  value={myFood.name}
                  onChange={(e) => setMyFood(s => ({ ...s, name: e.target.value }))}
                  className="bg-black/40 border-white/15 text-white text-sm"
                  data-testid="my-food-name"
                />
                {searching && (
                  <p className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">buscando...</p>
                )}
              </div>
              {searchResults.length > 0 && (
                <div className="rounded-md bg-black/40 border border-white/10 max-h-44 overflow-y-auto" data-testid="search-results">
                  {searchResults.map((f, i) => (
                    <button key={i} onClick={() => pickSearchResult(f)}
                      className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-white/5 last:border-0">
                      <p className="text-white text-sm font-medium">{f.name}</p>
                      <p className="text-[11px] text-gray-400">
                        {Math.round(f.calories || 0)} kcal · {+(f.protein || 0)}g P · {+(f.carbs || 0)}g C · {+(f.fats || f.fat || 0)}g G / 100g
                      </p>
                    </button>
                  ))}
                </div>
              )}
              {aiLoading && (
                <p className="text-[11px] text-cyan-300 text-center py-1">Buscando macros de &ldquo;{myFood.name}&rdquo;...</p>
              )}
              <div className="grid grid-cols-4 gap-2">
                <Input type="number" inputMode="decimal" placeholder="Kcal" value={myFood.calories}
                  onChange={(e) => setMyFood(s => ({ ...s, calories: e.target.value }))}
                  className="bg-black/40 border-white/15 text-white text-sm" data-testid="my-food-cal" />
                <Input type="number" inputMode="decimal" placeholder="Prot" value={myFood.protein}
                  onChange={(e) => setMyFood(s => ({ ...s, protein: e.target.value }))}
                  className="bg-black/40 border-white/15 text-white text-sm" data-testid="my-food-p" />
                <Input type="number" inputMode="decimal" placeholder="Carbs" value={myFood.carbs}
                  onChange={(e) => setMyFood(s => ({ ...s, carbs: e.target.value }))}
                  className="bg-black/40 border-white/15 text-white text-sm" data-testid="my-food-c" />
                <Input type="number" inputMode="decimal" placeholder="Grasas" value={myFood.fats}
                  onChange={(e) => setMyFood(s => ({ ...s, fats: e.target.value }))}
                  className="bg-black/40 border-white/15 text-white text-sm" data-testid="my-food-f" />
              </div>
              <Button onClick={submitMyFood} disabled={savingMyFood}
                className="w-full text-sm" style={{ background: 'linear-gradient(135deg, var(--brand-primary), #FF6B9D)', color: 'white' }}
                data-testid="save-my-food-btn">
                {savingMyFood ? 'Guardando...' : 'Guardar mi alimento'}
              </Button>
            </div>
          )}

          {!loading && filteredOptions.map((opt, idx) => (
            <button
              key={idx}
              onClick={() => applySwap(opt)}
              disabled={applying}
              className="w-full text-left p-3 rounded-lg border transition-all hover:scale-[1.01]"
              style={{
                background: opt.is_user_food
                  ? 'rgba(var(--brand-primary-glow),0.12)'
                  : opt.fits ? 'rgba(30,41,59,0.85)' : 'rgba(60,40,15,0.75)',
                borderColor: opt.is_user_food
                  ? 'rgba(var(--brand-primary-glow),0.50)'
                  : opt.fits ? 'rgba(255,255,255,0.12)' : 'rgba(245,158,11,0.45)',
              }}
              data-testid={`swap-option-${idx}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    {opt.fits
                      ? <FaCheck className="text-emerald-400 flex-shrink-0" />
                      : <FaExclamationTriangle className="text-amber-400 flex-shrink-0" />
                    }
                    <p className="text-white font-bold truncate text-base">{opt.name}</p>
                    {opt.is_user_food && <span className="text-[9px] uppercase text-pink-200 px-1.5 py-0.5 rounded bg-pink-500/30">Mi alimento</span>}
                  </div>
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                    <span className="font-extrabold text-lg" style={{ color: '#FF6B9D' }}>
                      {opt.grams}{opt.unit}
                    </span>
                    <span className="text-sm text-white/90">{Math.round(opt.macros.calories)} kcal</span>
                    <span className="text-sm text-white/70">{opt.macros.protein}g prot</span>
                    <span className="text-sm text-white/70">{opt.macros.carbs}g carb</span>
                    <span className="text-sm text-white/70">{opt.macros.fats}g grasa</span>
                  </div>
                  {opt.warning && (
                    <p className="text-[12px] text-amber-300 mt-2 leading-relaxed">{opt.warning}</p>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>

        <DialogFooter className="flex-shrink-0 mt-2 gap-2">
          {hasExistingSwap && (
            <Button onClick={removeSwap} disabled={removing} variant="outline" className="border-orange-500/30 text-orange-300 hover:bg-orange-500/10" data-testid="remove-swap-btn">
              <FaUndo className="mr-2" /> Restaurar original
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/20 text-white hover:bg-white/10">
            <FaTimes className="mr-2" /> Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
